import React, { useEffect, useMemo, useRef } from 'react';
import { TextField } from 'new/elements/TextField';
import {
  useVendorBuilder,
  VendorType,
} from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { Select } from 'new/elements/Select';
import { differenceBy, get, set, unset } from 'lodash';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import Typography from 'new/elements/Typography';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { Stack } from 'new/elements/Stack';
import classes from './VendorBuilder.module.scss';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';

interface FormControlBuilderProps {
  type: string;
  fields?: FormControlBuilderProps[];
  items?: any[];
  dependsOn?: { [key: string]: any };
  [key: string]: any;
}

const comparisons = {
  noteq(val1, val2) {
    return val1 !== val2;
  },
  exists(val1, val2) {
    return !!val1;
  },
};

export const FormControlBuilder = (props: FormControlBuilderProps) => {
  const {
    type,
    label,
    fields,
    required,
    hidden,
    inputType,
    startAdornment,
    prefix,
    items,
    defaultValue: dValue,
    dependsOn,
    gridDirection = 'row',
    ...rest
  } = props;

  const { name, helperText, description } = rest;

  const { vendor, onChange, errors } = useVendorBuilder();

  if (hidden) {
    return null;
  }

  if (dependsOn) {
    if (!isDependsOnValid(vendor, dependsOn)) {
      return null;
    }
  }

  const defaultValue = get(vendor, name) || dValue;

  let labelContent;
  if (typeof label === 'string') {
    labelContent =
      required || defaultValue === '' ? (
        <>
          {label + ' '}
          <Typography variant="caption" color="error">
            *
          </Typography>
        </>
      ) : (
        label
      );
  } else {
    labelContent = required ? (
      <>
        {label}
        <Typography variant="caption" color="error">
          *
        </Typography>
      </>
    ) : (
      label
    );
  }

  let fieldLabel = labelContent;

  if (!!description) {
    fieldLabel = (
      <KnowledgePanel alwaysShowUnderline knowledgeContent={description}>
        {fieldLabel}
      </KnowledgePanel>
    );
  }

  if (type === 'grid') {
    return (
      <Grid container spacing={2} direction={gridDirection}>
        <Grid item xs={4}>
          <FormControlBuilder {...fields[0]} />
        </Grid>
        <Grid item xs={8}>
          <FormControlBuilder {...fields[1]} />
        </Grid>
      </Grid>
    );
  }

  const onChangeText = useDebouncedCallback(
    (e) => {
      e.persist();
      onChange(e);
    },
    [onChange],
    700,
  );

  if (type === 'text') {
    return (
      <TextField
        defaultValue={defaultValue}
        autoComplete="off"
        label={fieldLabel}
        fullWidth
        startAdornment={
          !!prefix ? (
            <Stack className={classes.prefix} marginRight="8px">
              {!!prefix?.vendorPath ? get(vendor, prefix.vendorPath) + '.' : prefix}
            </Stack>
          ) : undefined
        }
        type={inputType}
        onChange={onChangeText}
        {...rest}
        error={!!errors[name]}
        helperText={errors[name] || ''}
      />
    );
  }

  if (type === 'select') {
    if (!items) {
      throw new Error('When setting "select" as type you should provide items');
    }
    return (
      <Select
        items={items}
        label={fieldLabel}
        autoComplete="off"
        defaultValue={defaultValue}
        fullWidth
        {...rest}
        error={!!errors[name]}
        helperText={errors[name] || ''}
        onChange={onChange}
      />
    );
  }

  if (type === 'checkbox') {
    return (
      <FormControlLabel
        onChange={onChange}
        control={<Checkbox defaultChecked={defaultValue} name={rest.name} />}
        label={fieldLabel}
      />
    );
  }

  return null;
};

const getLabelContent = (label, required, defaultValue) => {
  let content;
  if (typeof label === 'string') {
    content =
      required || defaultValue === '' ? (
        <>
          {label + ' '}
          <Typography variant="caption" color="error">
            *
          </Typography>
        </>
      ) : (
        label
      );
  } else {
    content = required ? (
      <>
        {label}
        <Typography variant="caption" color="error">
          *
        </Typography>
      </>
    ) : (
      label
    );
  }

  return content;
};

const isDependsOnValid = (vendor, dependsOn) => {
  return Object.keys(dependsOn).every((key) => {
    const value = get(vendor, key);
    const dependsOnValue = dependsOn[key];
    if (Array.isArray(dependsOnValue)) {
      return dependsOnValue.some((v) => v === value);
    }
    if (typeof dependsOnValue === 'object') {
      const { value: valueOn, comparison } = dependsOnValue;
      return comparisons[comparison]?.(valueOn, value);
    }
    return value === dependsOnValue;
  });
};

const fieldByDependsOn =
  (vendor: VendorType) =>
  (value: FormControlBuilderProps, index: number, array: FormControlBuilderProps[]) => {
    const { dependsOn } = value;
    if (dependsOn) {
      return isDependsOnValid(vendor, dependsOn);
    }
    return true;
  };

function flattenFields(fields) {
  const flatFields = [];
  fields.forEach((field) => {
    if (field.type !== 'grid') {
      flatFields.push(field);
    } else {
      field.fields.forEach((deepField) => flatFields.push(deepField));
    }
  });
  return flatFields;
}

export const FormBuilder = (props: {
  preventCleanUp?: boolean;
  fields: FormControlBuilderProps[];
}) => {
  const { vendor, updateVendor } = useVendorBuilder();
  const { fields, preventCleanUp } = props;

  const prevFields = useRef([]);

  const mapDependsOnKeys = useMemo(() => {
    return fields.reduce((keyMap, field) => {
      if (field.dependsOn) {
        Object.keys(field.dependsOn).forEach((key) => {
          keyMap[key] = true;
        });
      }
      return keyMap;
    }, {});
  }, [fields]);

  useEffect(() => {
    // Removing all fields when unmounting
    return () => {
      if (preventCleanUp) {
        return;
      }
      updateVendor((newVendor) => {
        const toAddFields = flattenFields(fields).filter(fieldByDependsOn(newVendor));
        toAddFields.forEach((field) => {
          unset(newVendor, field.name);
        });
      });
    };
  }, []);

  useEffect(() => {
    updateVendor((newVendor) => {
      const toAddFields = flattenFields(fields).filter(fieldByDependsOn(newVendor));
      const toAdd = differenceBy(toAddFields, prevFields.current, 'name');
      toAdd.forEach((field) => {
        const vendorValue = get(newVendor, field.name);
        if (field.defaultValue != undefined || field.required || vendorValue) {
          set(
            newVendor,
            field.name,
            vendorValue || field.defaultValue || (field.type === 'checkbox' ? false : ''),
          );
        }
      });
      prevFields.current = toAddFields;
    });

    return () => {
      if (preventCleanUp) {
        return;
      }
      updateVendor((newVendor) => {
        const toAddFields = flattenFields(fields).filter(fieldByDependsOn(newVendor));
        const toRemove = differenceBy(prevFields.current, toAddFields, 'name');
        toRemove.forEach((field) => {
          unset(newVendor, field.name);
        });
      });
    };
  }, [...Object.keys(mapDependsOnKeys).map((key) => get(vendor, key)), fields]);

  return (
    <>
      {fields.map((field, idx) => (
        <FormControlBuilder {...field} key={idx} />
      ))}
    </>
  );
};
